import { Box, CloseButton, Dialog, Portal, VStack } from "@chakra-ui/react";
import { useFormSubmitErrors } from "@features/errors/form-errors.tsx";
import { useResendButton } from "@features/two-fa/hooks/use-resend-button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { FormattedMessage, type MessageDescriptor, useIntl } from "react-intl";
import { z } from "zod";

import { otpCodeSchema, totpCodeSchema } from "@shared/libs/validators";
import { useFormatErrorMessages } from "@shared/libs/validators/hooks/use-format-error-messages";
import { CellPanel } from "@shared/ui/atoms";
import { Icon } from "@shared/ui/chakra";
import { Button } from "@shared/ui/chakra/buttons/button";
import { InputField } from "@shared/ui/chakra/forms/input-field";
import { ResendButton } from "@shared/ui/molecules/resend-button";
import { Cell } from "@shared/ui/organisms/cell";

import { transactionVerifyMessages } from "./messages";

export const transactionVerifySchema = z.object({
  otpCode: otpCodeSchema,
  totpCode: totpCodeSchema,
});

export type TransactionVerifyFormValues = z.infer<
  typeof transactionVerifySchema
>;

export type TransactionVerifyDetails = {
  items: { label: MessageDescriptor; text: string }[];
  type: "default";
};

type Props = {
  details: TransactionVerifyDetails;
  otpLabel: string;
  resendBlockedUntil: Date | string;
  onBack: VoidFunction;
  onResendOTP: () => Promise<void>;
  onSubmit: (data: TransactionVerifyFormValues) => Promise<void>;
};

export const TransactionVerifyForm = ({
  details,
  otpLabel,
  resendBlockedUntil,
  onBack,
  onResendOTP,
  onSubmit,
}: Props) => {
  const resendButtonProps = useResendButton({
    blockedUntil: resendBlockedUntil,
    onResend: onResendOTP,
  });
  const {
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    register,
    setValue,
  } = useForm<TransactionVerifyFormValues>({
    defaultValues: {
      otpCode: "",
      totpCode: "",
    },
    mode: "onTouched",
    resolver: zodResolver(transactionVerifySchema),
  });
  const { formatMessage } = useIntl();
  const reset = () => {
    setValue("totpCode", "");
  };
  const handleSubmitWithErrors = useFormSubmitErrors(
    onSubmit,
    [
      "ErrOtpExpired",
      "ErrOtpCodeMismatch",
      "ErrOtpInputAttemptsExceeded",
      "ErrIncorrectTOTPCode",
      "ErrUsedTotp",
      "ErrExpiredOtpSession",
    ],
    {
      ErrExpiredOtpSession: () => {
        onBack();
      },
      ErrIncorrectTOTPCode: () => reset(),
      ErrOtpCodeMismatch: () => reset(),
      ErrOtpExpired: () => reset(),
      ErrOtpInputAttemptsExceeded: () => reset(),
      ErrUsedTotp: () => reset(),
    },
  );
  const formatErrorMessage = useFormatErrorMessages();

  const handleClickPasteOTP = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setValue("otpCode", text.trim(), { shouldValidate: true });
      }
    } catch (err) {
      console.error(
        formatMessage(transactionVerifyMessages.clipboardError),
        err,
      );
    }
  };
  const handleClickPasteTOTP = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setValue("totpCode", text.trim(), { shouldValidate: true });
      }
    } catch (err) {
      console.error(
        formatMessage(transactionVerifyMessages.clipboardError),
        err,
      );
    }
  };

  return (
    <form noValidate onSubmit={handleSubmit(handleSubmitWithErrors)}>
      <VStack alignItems="stretch" gap="4" w="full">
        {details.items.length > 0 && (
          <CellPanel>
            <Dialog.Root placement="center">
              <Dialog.Trigger asChild>
                <Cell
                  helpIcon={<Icon color="gray.300" name="Info" />}
                  withChevron={false}
                >
                  {formatMessage(transactionVerifyMessages.details)}
                </Cell>
              </Dialog.Trigger>
              <Portal>
                <Dialog.Backdrop backgroundColor="blackAlpha.0/30" />
                <Dialog.Positioner>
                  <Dialog.Content>
                    <Dialog.Header>
                      <Dialog.Title>
                        {formatMessage(transactionVerifyMessages.details)}
                      </Dialog.Title>
                    </Dialog.Header>
                    <Dialog.Body>
                      <Box ml="-4.5">
                        <CellPanel hasIcons={false}>
                          {details.items.map((item) => (
                            <Cell
                              overline={formatMessage(item.label)}
                              withChevron={false}
                            >
                              {item.text}
                            </Cell>
                          ))}
                        </CellPanel>
                      </Box>
                    </Dialog.Body>
                    <Dialog.CloseTrigger asChild>
                      <CloseButton size="sm" />
                    </Dialog.CloseTrigger>
                  </Dialog.Content>
                </Dialog.Positioner>
              </Portal>
            </Dialog.Root>
          </CellPanel>
        )}

        <VStack
          alignItems="stretch"
          bg="bg.subtle"
          borderRadius="2xl"
          gap="2"
          p="6"
          w="full"
        >
          <InputField
            actionEl={
              <Button
                color="text.fg_accent"
                size="xs"
                variant="plain"
                onClick={handleClickPasteOTP}
              >
                {formatMessage(transactionVerifyMessages.pasteButton)}
              </Button>
            }
            errorText={formatErrorMessage(errors.otpCode?.message)}
            input={{ ...register("otpCode"), autoComplete: "off" }}
            inputBorderColor="border"
            label={otpLabel}
            placeholder={formatMessage(
              transactionVerifyMessages.otpCodePlaceholder,
            )}
            isInvalid={Boolean(errors.otpCode?.message)}
          />

          <ResendButton {...resendButtonProps} />
        </VStack>

        <VStack
          alignItems="stretch"
          bg="bg.subtle"
          borderRadius="2xl"
          gap="2"
          p="6"
          w="full"
        >
          <InputField
            actionEl={
              <Button
                color="text.fg_accent"
                size="xs"
                variant="plain"
                onClick={handleClickPasteTOTP}
              >
                {formatMessage(transactionVerifyMessages.pasteButton)}
              </Button>
            }
            actionOffset={90}
            errorText={formatErrorMessage(errors.totpCode?.message)}
            input={{ ...register("totpCode"), autoComplete: "off" }}
            inputBorderColor="border"
            label={formatMessage(transactionVerifyMessages.totpCodeLabel)}
            placeholder="6 значный код"
            isInvalid={Boolean(errors.totpCode?.message)}
          />
        </VStack>

        <Button
          disabled={!isValid || isSubmitting}
          loading={isSubmitting}
          type="submit"
        >
          <FormattedMessage {...transactionVerifyMessages.submitButton} />
        </Button>
      </VStack>
    </form>
  );
};
